import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';

import { Language, LanguageService, SPAConfiguration } from '@mobi/rwc-utils-ng-jslib';

import { LIST_LANGUAGE, PAGE_URL_PATH, PROTEKTA_URL_CONST, URL_CONST } from '../common.constant';
import { WINDOW } from '../core/provide-oiv-core.provider';
import { OivCoreSharedFacade } from '../facade/oiv-core-shared.facade';
import { RouteHelper } from '../route-helper';

export function getLanguageFromLocalStorage(userLanguage: Language) {
    const storedLanguage = localStorage.getItem('settings.language');
    if (storedLanguage) userLanguage = storedLanguage.replace(/"/g, '') as Language;
    return userLanguage;
}

export function setLocalStorageLanguage(languageParam: string) {
    const userLanguage = languageParam.replace(/"/g, '');
    localStorage.setItem('settings.language', userLanguage);
    return userLanguage as Language;
}

export function handleB2CConfig(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    RWC_CONFIG: SPAConfiguration,
    router: Router,
    window: Window,
): Language {
    let userLanguage: Language = 'de';
    if (RWC_CONFIG?.stage === 'prod') {
        const currentUrl = window.location.hostname;
        const matchedUrl =
            URL_CONST.find(url => currentUrl.indexOf(url.domain) > -1) ||
            PROTEKTA_URL_CONST.find(url => currentUrl.indexOf(url.domain) > -1);
        if (matchedUrl) userLanguage = matchedUrl.language as Language;
    } else {
        // Preprod B2C uses a single domain with path-based language routing (/:language/...).
        // Language is extracted from route.params by handleB2EConfig when :language is present.
        // This branch only runs for routes without :language (e.g. error pages) → fall back to localStorage.
        const languageParam = route.queryParams['language'];
        if (languageParam) {
            userLanguage = setLocalStorageLanguage(languageParam);
            const queryParams = { ...route.queryParams, language: null };
            router.navigate([state.url], { queryParams: queryParams, queryParamsHandling: 'merge' });
        } else {
            userLanguage = getLanguageFromLocalStorage(userLanguage);
        }
    }
    return userLanguage;
}

export function handleB2EConfig(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, router: Router): Language {
    let userLanguage: Language = 'de'; // default language is DE
    const languageParam = route.params['language'];

    if (isValidLanguage(languageParam)) {
        userLanguage = setLocalStorageLanguage(languageParam);
    } else if (isNavigableState(state.url)) {
        navigateToUserLanguageUrl(state.url, userLanguage, router); // if the language is not valid, navigate to the default language (DE)
    } else if (isErrorState(state.url)) {
        userLanguage = getLanguageFromLocalStorage(userLanguage);
    }
    return userLanguage;
}

function removeLanguageQueryParam(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, router: Router): void {
    if (!route.queryParams['language']) {
        return;
    }

    const queryParams = { ...route.queryParams, language: null };
    router.navigate([state.url], {
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
    });
}

function isValidLanguage(languageParam: string): boolean {
    return LIST_LANGUAGE.includes(languageParam);
}

function isNavigableState(url: string): boolean {
    return (
        !url.includes(PAGE_URL_PATH.NOT_FOUND) &&
        !url.includes(PAGE_URL_PATH.NOT_AVAILABLE) &&
        !url.includes(PAGE_URL_PATH.NO_PERMISSION)
    );
}

function isErrorState(url: string): boolean {
    return (
        url.includes(PAGE_URL_PATH.NOT_FOUND) ||
        url.includes(PAGE_URL_PATH.NOT_AVAILABLE) ||
        url.includes(PAGE_URL_PATH.NO_PERMISSION)
    );
}

function navigateToUserLanguageUrl(url: string, userLanguage: Language, router: Router): void {
    const helper = inject(RouteHelper);
    router.navigateByUrl(`/${userLanguage}${helper.removeFirstPath(url)}`);
}

// BMI: This is a resolver that sets the language based on the domain or query parameter.
export const languageResolver: ResolveFn<void> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): void => {
    const languageService = inject(LanguageService);
    const router = inject(Router);
    const facade = inject(OivCoreSharedFacade);
    const window = inject(WINDOW);
    // We need this workaround to fix issue when we change the language by domain.
    // Subscribe to the language service to trigger the language change.
    // It will get the initial language then subscribe to the language change when we use languageService.use()
    // Happens only in B2C
    languageService.language$.subscribe();

    const RWC_CONFIG: SPAConfiguration = (window.document.defaultView as Window).MOBI_RWC_CONFIG;
    let userLanguage: Language = 'de';

    if (RWC_CONFIG?.infraPreset === 'b2c') {
        // With B2C flavor, we have B2C and Protekta
        // For B2C, we have to set the language based on the domain (there are 3 domains)
        // For Protekta, we have to set the language based on the route parameter (there is only 1 domain)
        if (route.params['language']) {
            removeLanguageQueryParam(route, state, router);
            // Treat the route as B2E
            userLanguage = handleB2EConfig(route, state, router);
        } else {
            userLanguage = handleB2CConfig(route, state, RWC_CONFIG, router, window);
        }
    } else if (RWC_CONFIG?.infraPreset === 'b2e') {
        userLanguage = handleB2EConfig(route, state, router);
    }

    // Set the language in the language service and the mobiUILanguageService
    facade.language = userLanguage;
    facade.updateLibLanguage(userLanguage);
    facade.updateLinks(userLanguage);
};
